import React, { useState, useEffect } from 'react'
import { 
  Settings, Save, RefreshCw, Globe, 
  Clock, Bell, Mail, Shield, Database,
  Zap, Monitor, Smartphone, AlertCircle
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'

interface SystemConfig {
  // General Settings
  system_name: string
  timezone: string
  date_format: string
  time_format: '12h' | '24h'
  week_starts_on: 'sunday' | 'monday'
  
  // Chore Settings
  default_chore_points: number
  auto_assign_enabled: boolean
  auto_rotate_enabled: boolean
  rotation_time: string
  verification_required: boolean
  verification_timeout_hours: number
  
  // Notification Settings
  email_notifications_enabled: boolean
  push_notifications_enabled: boolean
  notification_digest_enabled: boolean
  digest_time: string
  reminder_hours_before: number
  
  // Point System
  exchange_rate: number  // Points per dollar (10 = 10 points = $1)
  streak_bonus_enabled: boolean
  streak_bonus_points: number
  perfect_week_bonus: number
  early_completion_bonus: number
  late_penalty_points: number
  
  // Security
  session_timeout_minutes: number
  require_password_change_days: number
  min_password_length: number
  two_factor_enabled: boolean
  
  // Integration
  google_calendar_sync: boolean
  apple_calendar_sync: boolean
  webhook_url?: string
  api_rate_limit: number
}

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona Time' },
  { value: 'UTC', label: 'UTC' }
]

export const SystemSettings: React.FC = () => {
  const [config, setConfig] = useState<SystemConfig>({
    system_name: 'HousePoints',
    timezone: 'America/New_York',
    date_format: 'MM/DD/YYYY',
    time_format: '12h',
    week_starts_on: 'sunday',
    default_chore_points: 5,
    auto_assign_enabled: true,
    auto_rotate_enabled: true,
    rotation_time: '07:00',
    verification_required: true,
    verification_timeout_hours: 24,
    email_notifications_enabled: false,
    push_notifications_enabled: true,
    notification_digest_enabled: false,
    digest_time: '18:00',
    reminder_hours_before: 2,
    exchange_rate: 10,
    streak_bonus_enabled: true,
    streak_bonus_points: 5,
    perfect_week_bonus: 20,
    early_completion_bonus: 2,
    late_penalty_points: 5,
    session_timeout_minutes: 60,
    require_password_change_days: 90,
    min_password_length: 8,
    two_factor_enabled: false,
    google_calendar_sync: false,
    apple_calendar_sync: false,
    api_rate_limit: 100
  })
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<'general' | 'chores' | 'notifications' | 'points' | 'security' | 'integration'>('general')

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    setLoading(true)
    try {
      const data = await apiClient.get('/api/system/settings')
      setConfig(data)
    } catch (error) {
      console.error('Failed to load system settings:', error)
      toast.error('Failed to load system settings')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveSettings = async () => {
    setSaving(true)
    try {
      await apiClient.put('/api/system/settings', config)
      toast.success('System settings saved successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handleResetToDefaults = () => {
    if (!confirm('Are you sure you want to reset all settings to defaults?')) return
    
    setConfig({
      system_name: 'HousePoints',
      timezone: 'America/New_York',
      date_format: 'MM/DD/YYYY',
      time_format: '12h',
      week_starts_on: 'sunday',
      default_chore_points: 5,
      auto_assign_enabled: true,
      auto_rotate_enabled: true,
      rotation_time: '07:00',
      verification_required: true,
      verification_timeout_hours: 24,
      email_notifications_enabled: false,
      push_notifications_enabled: true,
      notification_digest_enabled: false,
      digest_time: '18:00',
      reminder_hours_before: 2,
      exchange_rate: 10,
      streak_bonus_enabled: true,
      streak_bonus_points: 5,
      perfect_week_bonus: 20,
      early_completion_bonus: 2,
      late_penalty_points: 5,
      session_timeout_minutes: 60,
      require_password_change_days: 90,
      min_password_length: 8,
      two_factor_enabled: false,
      google_calendar_sync: false,
      apple_calendar_sync: false,
      api_rate_limit: 100
    })
    toast('Settings reset to defaults (not saved yet)')
  }

  const renderGeneralTab = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">System Name</label>
        <input
          type="text"
          value={config.system_name}
          onChange={(e) => setConfig({ ...config, system_name: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Timezone</label>
          <select
            value={config.timezone}
            onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
          >
            {TIMEZONES.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Time Format</label>
          <select
            value={config.time_format}
            onChange={(e) => setConfig({ ...config, time_format: e.target.value as any })}
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="12h">12-hour (AM/PM)</option>
            <option value="24h">24-hour</option>
          </select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Date Format</label>
          <select
            value={config.date_format}
            onChange={(e) => setConfig({ ...config, date_format: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Week Starts On</label>
          <select
            value={config.week_starts_on}
            onChange={(e) => setConfig({ ...config, week_starts_on: e.target.value as any })}
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="sunday">Sunday</option>
            <option value="monday">Monday</option>
          </select>
        </div>
      </div>
    </div>
  )

  const renderChoresTab = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">Default Chore Points</label>
        <input
          type="number"
          min="1"
          value={config.default_chore_points}
          onChange={(e) => setConfig({ ...config, default_chore_points: parseInt(e.target.value) })}
          className="w-full px-3 py-2 border rounded-lg"
        />
      </div>
      
      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.auto_assign_enabled}
            onChange={(e) => setConfig({ ...config, auto_assign_enabled: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Enable automatic chore assignment</span>
        </label>
        
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.auto_rotate_enabled}
            onChange={(e) => setConfig({ ...config, auto_rotate_enabled: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Enable automatic rotation</span>
        </label>
        
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.verification_required}
            onChange={(e) => setConfig({ ...config, verification_required: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Require parent verification for completed chores</span>
        </label>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Rotation Time</label>
          <input
            type="time"
            value={config.rotation_time}
            onChange={(e) => setConfig({ ...config, rotation_time: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Verification Timeout (hours)</label>
          <input
            type="number"
            min="1"
            value={config.verification_timeout_hours}
            onChange={(e) => setConfig({ ...config, verification_timeout_hours: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
      </div>
    </div>
  )

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.email_notifications_enabled}
            onChange={(e) => setConfig({ ...config, email_notifications_enabled: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Enable email notifications</span>
        </label>
        
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.push_notifications_enabled}
            onChange={(e) => setConfig({ ...config, push_notifications_enabled: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Enable push notifications</span>
        </label>
        
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.notification_digest_enabled}
            onChange={(e) => setConfig({ ...config, notification_digest_enabled: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Enable daily digest emails</span>
        </label>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Digest Time</label>
          <input
            type="time"
            value={config.digest_time}
            onChange={(e) => setConfig({ ...config, digest_time: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
            disabled={!config.notification_digest_enabled}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Reminder Before (hours)</label>
          <input
            type="number"
            min="1"
            value={config.reminder_hours_before}
            onChange={(e) => setConfig({ ...config, reminder_hours_before: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
      </div>
    </div>
  )

  const renderPointsTab = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">Exchange Rate (Points per Dollar)</label>
        <input
          type="number"
          min="1"
          value={config.exchange_rate}
          onChange={(e) => setConfig({ ...config, exchange_rate: parseInt(e.target.value) })}
          className="w-full px-3 py-2 border rounded-lg"
        />
        <p className="text-xs text-gray-500 mt-1">10 = 10 points equals $1.00</p>
      </div>
      
      <div>
        <label className="flex items-center mb-4">
          <input
            type="checkbox"
            checked={config.streak_bonus_enabled}
            onChange={(e) => setConfig({ ...config, streak_bonus_enabled: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Enable streak bonuses</span>
        </label>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Streak Bonus Points</label>
          <input
            type="number"
            min="0"
            value={config.streak_bonus_points}
            onChange={(e) => setConfig({ ...config, streak_bonus_points: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg"
            disabled={!config.streak_bonus_enabled}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Perfect Week Bonus</label>
          <input
            type="number"
            min="0"
            value={config.perfect_week_bonus}
            onChange={(e) => setConfig({ ...config, perfect_week_bonus: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Early Completion Bonus</label>
          <input
            type="number"
            min="0"
            value={config.early_completion_bonus}
            onChange={(e) => setConfig({ ...config, early_completion_bonus: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Late Penalty Points</label>
          <input
            type="number"
            min="0"
            value={config.late_penalty_points}
            onChange={(e) => setConfig({ ...config, late_penalty_points: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
      </div>
    </div>
  )

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Session Timeout (minutes)</label>
          <input
            type="number"
            min="5"
            value={config.session_timeout_minutes}
            onChange={(e) => setConfig({ ...config, session_timeout_minutes: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Password Expiry (days)</label>
          <input
            type="number"
            min="0"
            value={config.require_password_change_days}
            onChange={(e) => setConfig({ ...config, require_password_change_days: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg"
          />
          <p className="text-xs text-gray-500 mt-1">Set to 0 to disable</p>
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">Minimum Password Length</label>
        <input
          type="number"
          min="6"
          max="32"
          value={config.min_password_length}
          onChange={(e) => setConfig({ ...config, min_password_length: parseInt(e.target.value) })}
          className="w-full px-3 py-2 border rounded-lg"
        />
      </div>
      
      <div>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.two_factor_enabled}
            onChange={(e) => setConfig({ ...config, two_factor_enabled: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Enable two-factor authentication</span>
        </label>
      </div>
    </div>
  )

  const renderIntegrationTab = () => (
    <div className="space-y-6">
      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.google_calendar_sync}
            onChange={(e) => setConfig({ ...config, google_calendar_sync: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Enable Google Calendar sync</span>
        </label>
        
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.apple_calendar_sync}
            onChange={(e) => setConfig({ ...config, apple_calendar_sync: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Enable Apple Calendar sync</span>
        </label>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">Webhook URL</label>
        <input
          type="url"
          value={config.webhook_url || ''}
          onChange={(e) => setConfig({ ...config, webhook_url: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg"
          placeholder="https://example.com/webhook"
        />
        <p className="text-xs text-gray-500 mt-1">For external integrations</p>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">API Rate Limit (requests/hour)</label>
        <input
          type="number"
          min="10"
          value={config.api_rate_limit}
          onChange={(e) => setConfig({ ...config, api_rate_limit: parseInt(e.target.value) })}
          className="w-full px-3 py-2 border rounded-lg"
        />
      </div>
    </div>
  )

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">System Settings</h2>
        <p className="text-gray-600">Configure system-wide settings and preferences</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'general' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Globe className="w-4 h-4 inline mr-2" />
            General
          </button>
          <button
            onClick={() => setActiveTab('chores')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'chores' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Chores
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'notifications' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Bell className="w-4 h-4 inline mr-2" />
            Notifications
          </button>
          <button
            onClick={() => setActiveTab('points')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'points' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Zap className="w-4 h-4 inline mr-2" />
            Points
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'security' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Shield className="w-4 h-4 inline mr-2" />
            Security
          </button>
          <button
            onClick={() => setActiveTab('integration')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'integration' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Database className="w-4 h-4 inline mr-2" />
            Integration
          </button>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
              {activeTab === 'general' && renderGeneralTab()}
              {activeTab === 'chores' && renderChoresTab()}
              {activeTab === 'notifications' && renderNotificationsTab()}
              {activeTab === 'points' && renderPointsTab()}
              {activeTab === 'security' && renderSecurityTab()}
              {activeTab === 'integration' && renderIntegrationTab()}
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between">
        <button
          onClick={handleResetToDefaults}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Reset to Defaults
        </button>
        
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center disabled:bg-gray-400"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  )
}